"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FiAlertTriangle, FiCheck, FiExternalLink } from "react-icons/fi";
import Image from "next/image";
import { QualiphySetupRequired } from "@/components/qualiphy/qualiphy-setup-required";
import { Button, DateOfBirthInput } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectPills, MultiSelectTrigger } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { listOfStateAbbreviations } from "@/lib/state-abbreviations";
import { toast } from "@/lib/use-toast";
import { QualiphyGFEDetail } from "@/types/qualiphy";
import { qualiphyQueryOptions, useCreateQualiphyExamMutation, useQualiphyExamCatalog } from "@/query/integrations/qualiphy";
interface RequestQualiphyGFEModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientEmail?: string;
  patientPhone?: string;
  patientDateOfBirth?: string;
  patientState?: string;
  locationState?: string;
}
const stateOptions = [
// Qualiphy test state — used for testing per Qualiphy developer guide
{
  value: "TE",
  label: "TE - Test (Qualiphy Testing)"
}, ...Object.entries(listOfStateAbbreviations).map(([abbr, name]) => ({
  value: abbr,
  label: `${abbr} - ${name}`
}))];
export function RequestQualiphyGFEModal(t0) {
  const $ = _c(63);
  const {
    open,
    onOpenChange,
    patientId,
    patientEmail,
    patientPhone,
    patientDateOfBirth,
    patientState,
    locationState
  } = t0;
  let t1;
  if ($[0] !== locationState || $[1] !== patientState) {
    t1 = locationState?.toUpperCase() || patientState?.toUpperCase() || "";
    $[0] = locationState;
    $[1] = patientState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const defaultState = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [selectedExamIds, setSelectedExamIds] = useState(t2);
  const [teleState, setTeleState] = useState(defaultState);
  const [pharmacyId, setPharmacyId] = useState(null);
  const [orderSetId, setOrderSetId] = useState(null);
  const [successGfe, setSuccessGfe] = useState(null);
  const [dobValue, setDobValue] = useState(patientDateOfBirth ?? null);
  const [isDobValid, setIsDobValid] = useState(!!patientDateOfBirth);
  let t3;
  if ($[4] !== open) {
    t3 = {
      enabled: open
    };
    $[4] = open;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    exams,
    isConfigured: isQualiphyConfigured,
    isConfigurationLoading: loadingQualiphyConfiguration,
    isExamsLoading: loadingExams
  } = useQualiphyExamCatalog(t3);
  const createMutation = useCreateQualiphyExamMutation();
  const isPending = createMutation.isPending;
  const hasMissingEmail = !patientEmail;
  const hasMissingPhone = !patientPhone;
  const hasMissingContactInfo = hasMissingEmail || hasMissingPhone;
  const hasMissingDob = !patientDateOfBirth && (!dobValue || !isDobValid);
  const examOptions = exams?.map(_temp) ?? [];
  const selectedLevel2ExamIds = exams && selectedExamIds.length > 0 ? exams.filter(e_0 => selectedExamIds.includes(String(e_0.id)) && e_0.rxType === 2).map(_temp2) : [];
  const selectedLevel2ExamId = selectedLevel2ExamIds[0] ?? null;
  const hasLevel2Exam = selectedLevel2ExamId !== null;
  const hasMultipleLevel2 = selectedLevel2ExamIds.length > 1;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = qualiphyQueryOptions.partnerPharmacies();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = open && isQualiphyConfigured && hasLevel2Exam;
  let t6;
  if ($[7] !== t5) {
    t6 = {
      ...t4,
      enabled: t5
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: partnerPharmacies,
    isLoading: loadingPharmacies
  } = useQuery(t6);
  const t7 = qualiphyQueryOptions.treatmentPackages(pharmacyId ?? 0, selectedLevel2ExamId ?? 0);
  const t8 = open && isQualiphyConfigured && hasLevel2Exam && !!pharmacyId;
  let t9;
  if ($[9] !== t7 || $[10] !== t8) {
    t9 = {
      ...t7,
      enabled: t8
    };
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const {
    data: treatmentPackages,
    isLoading: loadingPackages
  } = useQuery(t9);
  let t10;
  if ($[12] !== createMutation || $[13] !== dobValue || $[14] !== hasLevel2Exam || $[15] !== hasMultipleLevel2 || $[16] !== isDobValid || $[17] !== isQualiphyConfigured || $[18] !== onOpenChange || $[19] !== orderSetId || $[20] !== patientDateOfBirth || $[21] !== patientEmail || $[22] !== patientId || $[23] !== patientPhone || $[24] !== pharmacyId || $[25] !== selectedExamIds || $[26] !== teleState) {
    t10 = async () => {
      if (!isQualiphyConfigured) {
        return;
      }
      if (selectedExamIds.length === 0) {
        toast({
          title: "Exam Required",
          description: "Please select at least one exam.",
          variant: "error"
        });
        return;
      }
      if (!teleState) {
        toast({
          title: "State Required",
          description: "Please select a state for the telehealth exam.",
          variant: "error"
        });
        return;
      }
      if (!patientDateOfBirth && (!dobValue || !isDobValid)) {
        toast({
          title: "Date of Birth Required",
          description: "Please enter the patient's date of birth",
          variant: "error"
        });
        return;
      }
      if (hasMultipleLevel2) {
        toast({
          title: "Only One Rx Exam Allowed",
          description: "Qualiphy allows only one Rx (Level 2) exam per meeting. Please remove the extras.",
          variant: "error"
        });
        return;
      }
      if (hasLevel2Exam && !pharmacyId) {
        toast({
          title: "Pharmacy Required",
          description: "This exam is a partner-pharmacy (Rx) exam and requires a pharmacy selection.",
          variant: "error"
        });
        return;
      }
      createMutation.mutate({
        patientId,
        examIds: selectedExamIds.map(Number),
        teleState,
        patientEmail,
        patientPhone,
        dateOfBirth: !patientDateOfBirth ? dobValue : undefined,
        pharmacyId: hasLevel2Exam ? pharmacyId : null,
        orderSetId: hasLevel2Exam ? orderSetId : null
      }, {
        onSuccess: data => {
          if (data) {
            setSuccessGfe(data);
          } else {
            toast({
              title: "Exam Invite Sent",
              description: "The patient will receive an email and SMS to complete the exam.",
              variant: "success"
            });
            onOpenChange(false);
          }
        }
      });
    };
    $[12] = createMutation;
    $[13] = dobValue;
    $[14] = hasLevel2Exam;
    $[15] = hasMultipleLevel2;
    $[16] = isDobValid;
    $[17] = isQualiphyConfigured;
    $[18] = onOpenChange;
    $[19] = orderSetId;
    $[20] = patientDateOfBirth;
    $[21] = patientEmail;
    $[22] = patientId;
    $[23] = patientPhone;
    $[24] = pharmacyId;
    $[25] = selectedExamIds;
    $[26] = teleState;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleSubmit = t10;
  let t11;
  let t12;
  if ($[28] !== defaultState || $[29] !== open || $[30] !== patientDateOfBirth) {
    t11 = () => {
      if (!open) {
        setSelectedExamIds([]);
        setTeleState(defaultState);
        setPharmacyId(null);
        setOrderSetId(null);
        setSuccessGfe(null);
        setDobValue(patientDateOfBirth ?? null);
        setIsDobValid(!!patientDateOfBirth);
      }
    };
    t12 = [open, defaultState, patientDateOfBirth];
    $[28] = defaultState;
    $[29] = open;
    $[30] = patientDateOfBirth;
    $[31] = t11;
    $[32] = t12;
  } else {
    t11 = $[31];
    t12 = $[32];
  }
  useEffect(t11, t12);
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setOrderSetId(null);
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== pharmacyId || $[35] !== selectedLevel2ExamId) {
    t14 = [pharmacyId, selectedLevel2ExamId];
    $[34] = pharmacyId;
    $[35] = selectedLevel2ExamId;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  useEffect(t13, t14);
  const T0 = Dialog;
  const T1 = DialogContent;
  const t15 = "max-w-lg";
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Image src="/logos/qualiphy-favicon.png" alt="Qualiphy" width={32} height={32} />;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = successGfe ? "Exam Invite Sent" : "Request Good Faith Exam";
  let t18;
  if ($[38] !== t17) {
    t18 = <DialogHeader><div className="flex items-center gap-3">{t16}<DialogTitle>{t17}</DialogTitle></div></DialogHeader>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const T2 = DialogBody;
  const t19 = successGfe ? <div className="flex flex-col items-center justify-center space-y-6 py-4 text-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-status-success-bg text-status-success-text"><FiCheck className="h-8 w-8" /></div><div className="space-y-2"><h3 className="text-h2">Invite Sent</h3><p className="text-body text-foreground-secondary">The patient will receive an email and SMS to complete the Good Faith Exam via Qualiphy.</p></div>{successGfe.meetingUrl && <Button variant="secondary" onClick={() => window.open(successGfe.meetingUrl, "_blank", "noopener,noreferrer")} icon={FiExternalLink}>Open Exam Link</Button>}<p className="text-caption text-foreground-secondary">Meeting ID: {successGfe.qualiphyMeetingUuid}</p></div> : loadingQualiphyConfiguration ? <div className="space-y-4 py-2" role="status" aria-label="Loading Qualiphy setup"><Skeleton className="h-20 w-full rounded-lg" /><Skeleton className="h-9 w-full rounded-md" /><Skeleton className="h-9 w-full rounded-md" /></div> : !isQualiphyConfigured ? <QualiphySetupRequired /> : <div className="space-y-4">{hasMissingContactInfo && <div className="rounded-lg border border-status-warning-text bg-status-warning-bg p-4"><div className="flex items-start gap-3"><FiAlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-status-warning-text" /><div className="flex-1"><h4 className="font-medium text-status-warning-text">Missing Patient Contact Information</h4><p className="mt-1 text-body text-status-warning-text">Qualiphy exams require patient contact information:</p><ul className="mt-2 list-inside list-disc text-body text-status-warning-text">{hasMissingEmail && <li>Email address</li>}{hasMissingPhone && <li>Phone number</li>}</ul></div></div></div>}{!patientDateOfBirth && <div className="space-y-1"><Label mandatory={true}>Date of Birth</Label><DateOfBirthInput value={dobValue ?? undefined} onChange={setDobValue} onValidityChange={setIsDobValid} maxDate={new Date()} disabled={hasMissingContactInfo || isPending} /><p className="text-caption text-foreground-secondary">Date of birth is required for Qualiphy exams</p></div>}<div className="space-y-1"><Label mandatory={true}>Exams</Label>{loadingExams ? <Skeleton className="h-9 w-full rounded-md" role="status" aria-label="Loading Qualiphy exams" /> : <MultiSelect options={examOptions} value={selectedExamIds} onChange={setSelectedExamIds} placeholder="Select exams..." searchPlaceholder="Search exams..." disabled={hasMissingContactInfo}><MultiSelectTrigger /><MultiSelectContent>{examOptions.map(_temp3)}</MultiSelectContent><MultiSelectPills /></MultiSelect>}</div><div className="space-y-1"><Label mandatory={true}>Telehealth State</Label><Select value={teleState} onValueChange={setTeleState} disabled={hasMissingContactInfo}><SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent><SelectGroup>{stateOptions.map(_temp4)}</SelectGroup></SelectContent></Select><p className="text-caption text-foreground-secondary">The state where the patient will complete the telehealth exam.</p></div>{hasLevel2Exam && <><div className="space-y-1"><Label mandatory={true}>Partner Pharmacy</Label><Select value={pharmacyId !== null ? String(pharmacyId) : ""} onValueChange={v => setPharmacyId(v ? Number(v) : null)} disabled={hasMissingContactInfo || loadingPharmacies}><SelectTrigger><SelectValue placeholder={loadingPharmacies ? "Loading pharmacies..." : "Select partner pharmacy"} /></SelectTrigger><SelectContent><SelectGroup>{(partnerPharmacies ?? []).map(_temp5)}</SelectGroup></SelectContent></Select><p className="text-caption text-foreground-secondary">Required for this Rx exam — the compounding pharmacy that will fulfill the prescription.</p></div><div className="space-y-1"><Label>Treatment Package (optional)</Label><Select value={orderSetId ?? "__none__"} onValueChange={v_0 => setOrderSetId(v_0 === "__none__" ? null : v_0)} disabled={hasMissingContactInfo || !pharmacyId || loadingPackages}><SelectTrigger><SelectValue placeholder={!pharmacyId ? "Pick a pharmacy first" : loadingPackages ? "Loading packages..." : "Let provider choose"} /></SelectTrigger><SelectContent><SelectGroup><SelectItem value="__none__">Let provider choose</SelectItem>{(treatmentPackages ?? []).map(_temp6)}</SelectGroup></SelectContent></Select><p className="text-caption text-foreground-secondary">Preselect a drug + dosage package, or leave blank to let the provider choose during the visit.</p></div></>}</div>;
  let t20;
  if ($[40] !== T2 || $[41] !== t19) {
    t20 = <T2>{t19}</T2>;
    $[40] = T2;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== handleSubmit || $[44] !== hasMissingContactInfo || $[45] !== hasMissingDob || $[46] !== isPending || $[47] !== isQualiphyConfigured || $[48] !== loadingQualiphyConfiguration || $[49] !== onOpenChange || $[50] !== successGfe) {
    t21 = <DialogFooter>{successGfe ? <Button variant="secondary" onClick={() => onOpenChange(false)} className="w-full">Done</Button> : loadingQualiphyConfiguration || !isQualiphyConfigured ? <Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button> : <><Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button><Button variant="primary" onClick={handleSubmit} isLoading={isPending} loadingText="Sending Invite..." disabled={hasMissingContactInfo || hasMissingDob || isPending}>Send Exam Invite</Button></>}</DialogFooter>;
    $[43] = handleSubmit;
    $[44] = hasMissingContactInfo;
    $[45] = hasMissingDob;
    $[46] = isPending;
    $[47] = isQualiphyConfigured;
    $[48] = loadingQualiphyConfiguration;
    $[49] = onOpenChange;
    $[50] = successGfe;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex items-center justify-end gap-1.5 px-6 pb-4"><span className="text-caption text-foreground-muted">Powered by</span><Image src="/logos/qualiphy-logo.png" alt="Qualiphy" width={80} height={22} className="h-4 w-auto" /></div>;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== T1 || $[54] !== t18 || $[55] !== t20 || $[56] !== t21) {
    t23 = <T1 className={t15}>{t18}{t20}{t21}{t22}</T1>;
    $[53] = T1;
    $[54] = t18;
    $[55] = t20;
    $[56] = t21;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== T0 || $[59] !== onOpenChange || $[60] !== open || $[61] !== t23) {
    t24 = <T0 open={open} onOpenChange={onOpenChange}>{t23}</T0>;
    $[58] = T0;
    $[59] = onOpenChange;
    $[60] = open;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  return t24;
}
function _temp6(pkg) {
  const label = [pkg.drugName, pkg.strength, pkg.size, pkg.durationDays ? `${pkg.durationDays}d` : null, pkg.price !== null && pkg.price !== undefined ? `$${(pkg.price / 100).toFixed(2)}` : null].filter(Boolean).join(" \xB7 ");
  return <SelectItem key={pkg.orderSetId} value={pkg.orderSetId}>{label || pkg.orderSetId}</SelectItem>;
}
function _temp5(p) {
  return <SelectItem key={p.pharmacyId} value={String(p.pharmacyId)}>{p.name}{p.type ? <span>{` (${p.type})`}</span> : <span>{""}</span>}</SelectItem>;
}
function _temp4(state) {
  return <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>;
}
function _temp3(exam) {
  return <MultiSelectItem key={exam.value} value={exam.value}>{exam.label}</MultiSelectItem>;
}
function _temp2(e_1) {
  return e_1.id;
}
function _temp(e) {
  const tag = e.rxType === 2 ? " \xB7 Rx" : "";
  const title = e.title ?? "Untitled exam";
  return {
    value: String(e.id),
    label: e.price ? `${title} ($${e.price})${tag}` : `${title}${tag}`
  };
}